import { useState } from "react";
import { motion, AnimatePresence } from "./motion";
import {
  ChevronDown, ArrowRight, Sparkles, ArrowDown, ArrowRight as ArrowR, Loader2,
  Activity, CalendarHeart, Camera, Compass, Flame, ShieldCheck, Zap,
} from "lucide-react";
import { Reveal, Overline, CTA, ChatBubble, GlowOrb } from "./ui";
import { getAppUrl } from "@/urls";
import { STEPS, FEATURES, EXAMPLES, FAQS, DEMO, COACHING_CARDS } from "./data";
import { CoachingCard } from "./CoachingCard";

// Explicit map of the only icons FEATURES references — avoids `import * as Icons`
// which pulls the entire lucide-react library (~135 kB gzip) into the bundle.
// Batch 138 — HeartHandshake left with the card that sold Build Something
// Real, a direction ENABLE_EXPERIMENTAL_DIRECTIONS has never turned on.
// Compass and Camera arrive with the two shipped things the page never
// mentioned. Still an explicit map, for the reason below.
const FEATURE_ICONS = { Activity, CalendarHeart, Camera, Compass, Flame, ShieldCheck, Sparkles, Zap };

const SectionHead = ({ label, title, sub }) => (
  <Reveal className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
    <Overline>{label}</Overline>
    <h2 className="mt-4 font-['Bebas_Neue'] text-4xl sm:text-5xl md:text-6xl tracking-wide leading-[0.95] text-[#F0F0F0]">
      {title}
    </h2>
    {sub && <p className="mt-4 font-['Inter'] text-[#A8A8B8] text-base md:text-lg leading-relaxed">{sub}</p>}
  </Reveal>
);

/* ── Interactive demo (near top, no account) ───────────── */
export const Demo = () => {
  const [active, setActive] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | thinking | done
  const pick = (i) => {
    setActive(i); setPhase("thinking");
    setTimeout(() => setPhase("done"), 850);
  };
  const d = active != null ? DEMO[active] : null;
  return (
    <section id="demo" className="relative py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-6">
        <SectionHead label="Try it now" title="See SparkCue think — in one tap" sub="Pick a situation. Watch it read the moment and hand you the move." />
        <Reveal>
          <div className="rounded-3xl bg-[#1A1A1F] border border-[#2A2A35] p-5 md:p-8">
            <div className="flex flex-wrap gap-2.5 justify-center mb-7">
              {DEMO.map((opt, i) => (
                <button
                  key={i} onClick={() => pick(i)} data-testid={`demo-option-${i}`}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-all border ${
                    active === i ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-[#2A2A35] text-[#A8A8B8] hover:border-[#C9A84C]/50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <AnimatePresence mode="wait">
              {!d ? (
                <motion.p key="hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-center text-[#7A7A8C] text-sm py-8 font-['Inter']">
                  Choose a situation above to see SparkCue's recommended reply.
                </motion.p>
              ) : (
                <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="grid md:grid-cols-2 gap-4 items-stretch">
                  <div className="rounded-2xl bg-[#0D0D0F] border border-[#2A2A35] p-5">
                    <p className="text-[#7A7A8C] text-[11px] uppercase tracking-wider font-bold mb-3">Their message</p>
                    <ChatBubble from="them" text={d.them} />
                  </div>
                  <div className="rounded-2xl bg-[#C9A84C]/[0.06] border border-[#C9A84C]/30 p-5 flex flex-col">
                    <div className="flex items-center gap-1.5 mb-3">
                      <Sparkles size={12} className="text-[#C9A84C]" />
                      <p className="text-[#C9A84C] text-[11px] uppercase tracking-wider font-bold">SparkCue says</p>
                    </div>
                    {phase === "thinking" ? (
                      <div className="flex items-center gap-2 text-[#7A7A8C] text-sm py-3">
                        <Loader2 size={15} className="animate-spin text-[#C9A84C]" /> reading the moment…
                      </div>
                    ) : (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                        <ChatBubble from="you" text={d.spark} />
                        <p className="mt-3 flex items-center gap-1.5 text-[#C9A84C]/80 text-xs font-['Inter']">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {d.signal}
                        </p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-7 text-center">
              <CTA href={getAppUrl()} testid="demo-cta">Try it on your own chat <ArrowRight size={18} /></CTA>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ── The coaching, in the app's own format ─────────────── */
// Batch 139 — the section that replaced Showcase. Showcase held three chats
// ending on an invented reply from her plus a cue phrased as a result. Once
// both were removed under ruling 4 what remained was a message, a reply and a
// note about the read, which is what these cards do in the real format, better.
// A weaker duplicate three sections above the real thing costs the real thing
// its credibility, so it was cut rather than trimmed.
export const Coaching = () => (
  <section id="coaching" className="relative py-20 md:py-32 bg-[#0b0b0d]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <SectionHead
        label="What you actually get"
        title="The move, the reason, and the words."
        sub="This is the screen, not a mockup of it. The third one is the move most apps cannot make."
      />
      <div className="grid md:grid-cols-3 gap-5 md:gap-8 items-start">
        {COACHING_CARDS.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.12}>
            <CoachingCard card={c} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ── How it works ───────────────────────────────────────── */
export const Steps = () => (
  <section id="how" className="relative py-20 md:py-32 bg-[#0b0b0d]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <SectionHead label="How it works" title="Three taps to the perfect reply" />
      <div className="grid md:grid-cols-3 gap-5 md:gap-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.15}>
            <div className="relative h-full rounded-3xl bg-[#1A1A1F] border border-[#2A2A35] p-8 md:p-10 hover:-translate-y-1 hover:border-[#C9A84C]/40 transition-all duration-300">
              <span className="font-['Bebas_Neue'] text-6xl md:text-7xl text-[#C9A84C]/25 leading-none">{s.n}</span>
              <h3 className="mt-3 font-['Bebas_Neue'] text-2xl md:text-3xl text-[#F0F0F0] tracking-wide">{s.title}</h3>
              <p className="mt-3 font-['Inter'] text-[#A8A8B8] leading-relaxed">{s.body}</p>
              {i < STEPS.length - 1 && <ArrowDown className="md:hidden mx-auto mt-6 text-[#C9A84C]/40" size={20} />}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ── Features ───────────────────────────────────────────── */
export const Features = () => (
  <section id="features" className="relative py-20 md:py-32 overflow-hidden">
    <GlowOrb className="w-[460px] h-[460px] -right-32 top-1/3 opacity-40" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <SectionHead label="Why it's different" title="It doesn't write texts. It reads people." sub="A conversation engine, not a reply generator. That's the whole difference." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {FEATURES.map((f, i) => {
          const Icon = FEATURE_ICONS[f.icon] || Sparkles;
          return (
            <Reveal key={f.title} delay={(i % 3) * 0.08} className={i === 0 ? "lg:col-span-2" : ""}>
              <div className="h-full rounded-3xl bg-[#1A1A1F] border border-[#2A2A35] p-6 md:p-7 hover:-translate-y-1 hover:border-[#C9A84C]/40 transition-all duration-300 group">
                <div className="w-11 h-11 rounded-xl bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center mb-4 group-hover:bg-[#C9A84C]/20 transition-colors">
                  <Icon size={20} className="text-[#C9A84C]" strokeWidth={1.6} />
                </div>
                <h3 className="font-['Bebas_Neue'] text-xl md:text-2xl text-[#F0F0F0] tracking-wide">{f.title}</h3>
                <p className="mt-2 font-['Inter'] text-[#A8A8B8] text-sm leading-relaxed">{f.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ── Before / After ─────────────────────────────────────── */
export const Examples = () => (
  <section id="examples" className="relative py-20 md:py-32 bg-[#0b0b0d]">
    <div className="max-w-5xl mx-auto px-6 lg:px-8">
      <SectionHead label="The difference" title="Same message. Two very different replies." sub="What most people reach for, and what SparkCue would say instead." />
      <div className="space-y-6">
        {EXAMPLES.map((ex, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-center rounded-3xl bg-[#1A1A1F] border border-[#2A2A35] p-5 md:p-6 hover:border-[#C9A84C]/30 transition-colors">
              <div className="rounded-2xl bg-[#0D0D0F] border border-[#2A2A35] p-5 opacity-80">
                <p className="text-[#7A7A8C] text-[11px] uppercase tracking-wider font-bold mb-3">Their message</p>
                <ChatBubble from="them" text={ex.them} />
                <p className="text-[#7A7A8C] text-[11px] uppercase tracking-wider font-bold mt-4 mb-2">What most people send</p>
                <p className="text-[#6a6a78] text-sm italic font-['Inter'] line-through decoration-[#444]/60">"{ex.typical}"</p>
              </div>

              <div className="hidden md:flex flex-col items-center text-[#C9A84C]">
                <ArrowR size={22} />
              </div>

              <div className="rounded-2xl bg-[#C9A84C]/[0.06] border border-[#C9A84C]/30 p-5 shadow-[0_0_28px_rgba(201,168,76,0.08)]">
                <div className="flex items-center gap-1.5 mb-3">
                  <Sparkles size={12} className="text-[#C9A84C]" />
                  <p className="text-[#C9A84C] text-[11px] uppercase tracking-wider font-bold">SparkCue says</p>
                </div>
                <ChatBubble from="you" text={ex.spark} />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1} className="text-center mt-12">
        {/* Batch 138 — this scrolled to a section whose own button opens the
            app, so it took two clicks to do what six other controls on this
            page do in one. It was also the only use of scrollToId in this
            file, so the import went with it. */}
        <CTA href={getAppUrl()} testid="examples-cta">
          <Sparkles size={16} /> Get replies like these
        </CTA>
      </Reveal>
    </div>
  </section>
);

/* ── Emotional ──────────────────────────────────────────── */
export const Emotion = () => (
  <section className="relative py-28 md:py-40 overflow-hidden">
    <GlowOrb className="w-[640px] h-[360px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 opacity-50" />
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <Sparkles className="mx-auto mb-7 text-[#C9A84C]" size={26} />
      </Reveal>
      {[
        "Every connection starts with one message.",
        "One reply can create attraction.",
        "One reply can lose it.",
      ].map((line, i) => (
        <Reveal key={i} delay={0.12 + i * 0.14}>
          <p className={`font-['Bebas_Neue'] tracking-wide leading-[1.05] ${
            i === 2 ? "text-[#C9A84C]" : "text-[#F0F0F0]"
          } text-3xl sm:text-4xl md:text-5xl`}>
            {line}
          </p>
        </Reveal>
      ))}
      <Reveal delay={0.6}>
        <p className="mt-8 font-['Inter'] text-[#A8A8B8] text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
          SparkCue helps you feel the difference — <span className="text-[#F0F0F0]">before you press send.</span>
        </p>
      </Reveal>
    </div>
  </section>
);

/* ── FAQ ────────────────────────────────────────────────── */
export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-20 md:py-32">
      <div className="max-w-3xl mx-auto px-6">
        <SectionHead label="Questions" title="Everything you're wondering" />
        <div className="space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={i} delay={i * 0.04}>
                <div className="rounded-2xl bg-[#1A1A1F] border border-[#2A2A35] overflow-hidden">
                  <button onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-trigger-${i}`}
                    className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-5 text-left">
                    <span className="font-['Inter'] font-semibold text-[#F0F0F0] text-[15px] md:text-lg">{f.q}</span>
                    <ChevronDown size={20} className={`text-[#C9A84C] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
                        <p className="px-5 md:px-6 pb-5 font-['Inter'] text-[#A8A8B8] leading-relaxed">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ── Final CTA ──────────────────────────────────────────── */
export const FinalCta = () => (
  <section className="relative py-28 md:py-40 overflow-hidden bg-[#08080a]">
    <GlowOrb className="w-[600px] h-[300px] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 opacity-60" />
    <div className="relative max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <h2 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl text-[#F0F0F0] tracking-wide leading-[0.92]">
          The right first reply<br /><span className="text-[#C9A84C]">changes everything.</span>
        </h2>
        <p className="mt-6 font-['Inter'] text-[#A8A8B8] text-lg max-w-xl mx-auto">
          Stop overthinking every text. Let SparkCue show you the move.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
          {/* Batch 136 — the control beside this one was labelled with the
              name of a list that no longer exists. Naming a waitlist on the
              last screen before someone leaves is the defect itself. */}
          <CTA href={getAppUrl()} testid="final-start-free">Start Free <ArrowRight size={18} /></CTA>
        </div>
      </Reveal>
    </div>
  </section>
);
